import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Briefcase, User, ArrowLeft, ArrowRight, MailCheck, Check } from "lucide-react";
import Captcha from "@/components/app/Captcha";
import { AuthFooter } from "@/components/app/AuthFooter";
import { ResendVerification } from "@/components/app/ResendVerification";

const CARDS = [
  { k: "bisnis", icon: Briefcase, title: "Keuangan Bisnis", desc: "Pembukuan usaha: pemasukan, invoice, pengeluaran, kas & bank, laporan laba rugi dan pajak usaha.",
    points: ["Invoice & piutang", "Laporan keuangan", "PPh 0,5% & 22%"] },
  { k: "pribadi", icon: User, title: "Keuangan Pribadi", desc: "Catat keuangan pribadi: dompet & rekening, harta, utang, dan bantu isi SPT Tahunan 1770.",
    points: ["Akun & dompet", "Harta & utang", "Asisten SPT Coretax"] },
];

const ChoiceCard = ({ c, onPick }) => {
  const Icon = c.icon;
  return (
    <button type="button" onClick={() => onPick(c.k)} data-testid={`register-card-${c.k}`}
      className="reg-card group text-left rounded-2xl border-2 border-slate-200 bg-white p-6 flex flex-col gap-4">
      <span className="h-12 w-12 rounded-xl flex items-center justify-center transition-colors duration-200 group-hover:text-white"
        style={{ background: "var(--brand-soft)", color: "var(--brand)" }}><Icon size={22} className="reg-card-icon"/></span>
      <span>
        <span className="block text-lg font-semibold text-slate-900">{c.title}</span>
        <span className="block text-sm text-slate-500 mt-1.5 leading-relaxed">{c.desc}</span>
      </span>
      <span className="space-y-1.5">
        {c.points.map((p) => <span key={p} className="flex items-center gap-2 text-xs text-slate-600"><Check size={13} style={{ color: "var(--brand)" }}/>{p}</span>)}
      </span>
      <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: "var(--brand)" }}>
        Pilih <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1"/>
      </span>
    </button>
  );
};

const empty = { name: "", email: "", no_telp: "", workspace_name: "", workspace_type: "hostnesia", password: "", confirm: "" };

function RegisterForm({ module, bizTypes, onBack, onDone }) {
  const [f, setF] = useState(empty);
  const [captcha, setCaptcha] = useState("");
  const [capKey, setCapKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) return toast.error("Password minimal 8 karakter");
    if (f.password !== f.confirm) return toast.error("Konfirmasi password tidak sama");
    setLoading(true);
    try {
      await api.post("/auth/register", { name: f.name, email: f.email, no_telp: f.no_telp, workspace_name: f.workspace_name,
        workspace_type: module === "pribadi" ? "pribadi" : f.workspace_type, password: f.password, captcha_token: captcha });
      onDone(f.email);
    } catch (err) { toast.error(formatApiError(err)); setCaptcha(""); setCapKey((k) => k + 1); } finally { setLoading(false); }
  };
  const field = (k, label, props = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={`reg-${k}`}>{label}</Label>
      <Input id={`reg-${k}`} data-testid={`register-${k}`} value={f[k]} onChange={upd(k)} {...props}/>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="register-form">
      <button type="button" onClick={onBack} data-testid="register-back" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft size={15}/> Ganti jenis akun
      </button>
      <div className="inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: "var(--brand-soft)", color: "var(--brand)" }} data-testid="register-module-badge">
        {module === "pribadi" ? "Keuangan Pribadi" : "Keuangan Bisnis"}
      </div>
      {field("name", "Nama Lengkap", { required: true, placeholder: "Nama Anda" })}
      <div className="grid sm:grid-cols-2 gap-4">
        {field("email", "Email", { type: "email", required: true, placeholder: "nama@email.com" })}
        {field("no_telp", "No. Telepon / WA", { placeholder: "08xxxxxxxxxx" })}
      </div>
      {module === "bisnis" && (
        <div className="space-y-1.5">
          <Label>Jenis Bisnis</Label>
          <div className="grid grid-cols-2 gap-2" data-testid="register-biz-types">
            {bizTypes.map((t) => (
              <button key={t.code} type="button" onClick={() => setF({ ...f, workspace_type: t.code })} data-testid={`register-biz-${t.code}`}
                className={`text-sm px-3 py-2.5 rounded-lg border-2 transition-colors duration-150 ${f.workspace_type === t.code ? "border-[color:var(--brand)] text-[color:var(--brand)] bg-[color:var(--brand-soft)]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {field("workspace_name", module === "pribadi" ? "Nama Workspace" : "Nama Usaha", { required: true, placeholder: module === "pribadi" ? "Mis. Keuangan Keluarga Budi" : "Mis. Toko Maju Jaya" })}
      <div className="grid sm:grid-cols-2 gap-4">
        {field("password", "Password", { type: "password", required: true, autoComplete: "new-password", placeholder: "Min. 8 karakter" })}
        {field("confirm", "Ulangi Password", { type: "password", required: true, autoComplete: "new-password" })}
      </div>
      <Captcha key={capKey} onToken={setCaptcha}/>
      <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full brand-btn rounded-sm">
        {loading ? "Mendaftar..." : "Daftar Akun"}
      </Button>
    </form>
  );
}

export default function Register() {
  const [module, setModule] = useState(null);
  const [doneEmail, setDoneEmail] = useState("");
  const [bizTypes, setBizTypes] = useState([]);
  useEffect(() => { api.get("/auth/register-options").then((r) => setBizTypes(r.data.business_types || [])).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex-1 w-full max-w-3xl mx-auto px-5 py-10 md:py-16">
        <div className="mb-8 reg-fade">
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Daftar Akun</h1>
          <p className="text-sm text-slate-500 mt-2">
            {doneEmail ? "Satu langkah lagi." : module ? "Lengkapi data diri untuk membuat akun." : "Pilih jenis akun yang ingin Anda buat."}
          </p>
        </div>
        {doneEmail ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center reg-fade" data-testid="register-success">
            <span className="mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-4" style={{ background: "var(--brand-soft)", color: "var(--brand)" }}><MailCheck size={26}/></span>
            <h2 className="text-lg font-semibold text-slate-900">Cek email Anda</h2>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">Kami mengirim link verifikasi ke <b>{doneEmail}</b>. Setelah email terverifikasi, akun Anda berstatus <b>Menunggu</b> persetujuan Super Admin. Anda akan mendapat email setelah disetujui.</p>
            <div className="max-w-xs mx-auto mt-6"><ResendVerification email={doneEmail}/></div>
            <Link to="/login" className="inline-block mt-6 text-sm font-medium text-[color:var(--brand)] hover:underline" data-testid="register-to-login">Kembali ke halaman Masuk</Link>
          </div>
        ) : module ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 reg-fade">
            <RegisterForm module={module} bizTypes={bizTypes} onBack={() => setModule(null)} onDone={setDoneEmail}/>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5" data-testid="register-choices">
            {CARDS.map((c, i) => <div key={c.k} className="reg-fade flex" style={{ animationDelay: `${i * 80}ms` }}><ChoiceCard c={c} onPick={setModule}/></div>)}
          </div>
        )}
        {!doneEmail && (
          <p className="text-sm text-slate-500 mt-8">Sudah punya akun? <Link to="/login" className="text-[color:var(--brand)] font-medium hover:underline" data-testid="register-login-link">Masuk</Link></p>
        )}
      </div>
      <AuthFooter/>
    </div>
  );
}
